import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { IconX } from '@tabler/icons-react';
import { yupResolver } from '@hookform/resolvers/yup';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import Box from '@mui/material/Box';
import { IconButton, Typography } from '@mui/material';
import DialogContent from '@mui/material/DialogContent';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import { LoadingButton } from '@mui/lab';

import {
  DuplicateDocumentTemplateFormFields,
  duplicateTemplateValidationSchema,
} from '@features/admin-multi-document-agent/utils/demand-letter/duplicate-template-validator';

import { FormInput } from '@components/FormInput/ui/FormInput';
import useToast from '@hooks/useToast';
import {
  BackendError,
  extractBackendError,
} from '@utils/handle-backend-error.ts';
import { MultiDocumentAgentTemplate } from '@app/types/admin';
import { duplicateTemplate } from '@features/admin-multi-document-agent/utils';

interface DuplicateTemplateModalProps {
  open: boolean;
  onClose: () => void;
  template: MultiDocumentAgentTemplate;
  onSuccess: () => void;
}

function DuplicateTemplateModal({
  open,
  onClose,
  template,
  onSuccess,
}: DuplicateTemplateModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const methods = useForm<DuplicateDocumentTemplateFormFields>({
    resolver: yupResolver(duplicateTemplateValidationSchema),
    mode: 'onChange',
  });
  const { showError, showSuccess } = useToast();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = methods;

  const onSubmit = async (data: DuplicateDocumentTemplateFormFields) => {
    setIsLoading(true);
    try {
      const response = await duplicateTemplate(template, data);
      showSuccess(
        'Success',
        `Your new ${response.name} template has been created successfully`
      );
      onSuccess();
    } catch (error) {
      const errorMessage = extractBackendError(error as BackendError);
      console.error(error);
      showError('Error', errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <FormProvider {...methods}>
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="h3">
                {`Duplicate Template - ${template.name}`}
              </Typography>
            </Box>
            <IconButton onClick={onClose}>
              <IconX />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid
              container
              spacing={2}
              alignItems="center"
              justifyContent="center"
            >
              <Grid item xs={12}>
                <FormInput
                  type="input"
                  required
                  labelText="Document Name"
                  placeholder="Please enter the new template name"
                  errorText={
                    errors?.name?.message ? errors?.name?.message : errors?.name
                  }
                  {...register('name')}
                />
              </Grid>
            </Grid>
          </form>
        </DialogContent>
        <DialogActions>
          <Box
            paddingX={3}
            display="flex"
            justifyContent="space-between"
            gap={2}
          >
            <Button variant="text" onClick={onClose}>
              Cancel
            </Button>
            <LoadingButton
              variant="contained"
              onClick={handleSubmit(onSubmit)}
              loading={isLoading}
            >
              Duplicate
            </LoadingButton>
          </Box>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}

export default DuplicateTemplateModal;
